// Local acoustic reference only. A bounded lag search estimates the component
// of post-AEC microphone audio coherent with recently scheduled output. Digital
// output amplitude alone says nothing about its level at the microphone.
const RATE = 4000;
// Search every downsampled sample: 1 ms steps alias voiced harmonics.
const STEP = 1;
type Clip = { start: number; samples: Float32Array };

function resample(samples: Float32Array | Int16Array, rate: number, scale: number) {
  const result = new Float32Array(Math.floor((samples.length * RATE) / rate));
  for (let i = 0; i < result.length; i++) {
    const start = Math.floor((i * rate) / RATE);
    const end = Math.floor(((i + 1) * rate) / RATE);
    let sum = 0;
    for (let j = start; j < end; j++) sum += samples[j] * scale;
    result[i] = sum / (end - start);
  }
  return result;
}

function coherentEnergy(mic: Float32Array, output: Float32Array) {
  let inputEnergy = 0;
  for (const value of mic) inputEnergy += value * value;
  if (!inputEnergy) return 0;
  let best = 0;
  for (let lag = 0; lag + mic.length <= output.length; lag += STEP) {
    let dot = 0;
    let energy = 0;
    for (let i = 0; i < mic.length; i++) {
      const value = output[lag + i];
      dot += mic[i] * value;
      energy += value * value;
    }
    if (energy > 1e-8) best = Math.max(best, (dot * dot) / (inputEnergy * energy));
  }
  return Math.sqrt(Math.min(1, best));
}

export function createEchoReference(context: AudioContext) {
  const clips: Clip[] = [];
  const horizon = () =>
    Math.max(0, context.outputLatency || 0) + Math.max(0, context.baseLatency || 0) + 0.4;
  function retire() {
    const before = context.currentTime - horizon() - 0.1;
    while (clips.length && clips[0].start + clips[0].samples.length / RATE < before) clips.shift();
  }
  return {
    append(buffer: AudioBuffer, start: number) {
      retire();
      clips.push({ start, samples: resample(buffer.getChannelData(0), buffer.sampleRate, 1) });
    },
    level(samples: Int16Array, sampleRate: number, endedAgoMs: number) {
      retire();
      const mic = resample(samples, sampleRate, 1 / 32768);
      const end = context.currentTime - endedAgoMs / 1000;
      const start = end - horizon() - mic.length / RATE;
      const output = new Float32Array(Math.ceil((end - start) * RATE));
      for (const clip of clips) {
        const offset = Math.round((clip.start - start) * RATE);
        const from = Math.max(0, -offset);
        const to = Math.min(clip.samples.length, output.length - offset);
        for (let i = from; i < to; i++) output[offset + i] = clip.samples[i];
      }
      let energy = 0;
      for (const value of samples) energy += (value / 32768) ** 2;
      const rms = Math.sqrt(energy / Math.max(1, samples.length));
      return rms * coherentEnergy(mic, output);
    },
    clear() {
      clips.length = 0;
    },
  };
}
